import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import "flag-icon-css/css/flag-icons.css";
import { RootState, AppDispatch } from "../../../../../redux/store";
import { updateConfig } from "./posConfigSlice";
import { POSLanguageItem } from "./constants";
import { DraggableCardList } from "./DraggableCardList";
import { getLanguageIcon } from "./posIconUtils";
import { useIsItemDisabledInGeneral } from "./useIsItemDisabledInGeneral";

const LANG_CODE_TO_KEY: Record<string, string> = {
    fr: "French",
    en: "English",
    es: "Spanish",
    it: "Italian",
    de: "German",
    nl: "Dutch",
    pt: "Portuguese",
};

export function Language() {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const items = useSelector(
        (state: RootState) => state.posConfig.config.Language.items
    );
    const isItemDisabledInGeneral = useIsItemDisabledInGeneral();

    const getLanguageTitle = React.useCallback(
        (item: POSLanguageItem) => {
            const code = (item.name || "").toLowerCase();
            const key = LANG_CODE_TO_KEY[code] || item.title;
            return t(key);
        },
        [t]
    );

    const handleToggle = React.useCallback(
        (index: number) => {
            const item = items[index];
            if (isItemDisabledInGeneral(item.id)) return;
            if ((item.isDefault || item.isFavorite) && item.active) return;
            const activeCount = items.filter(
                (i: POSLanguageItem) => i.active
            ).length;
            if (item.active && activeCount <= 1) return;

            const newItems = items.map((it: POSLanguageItem, i: number) =>
                i === index ? { ...it, active: !it.active } : { ...it }
            );
            dispatch(
                updateConfig({ path: ["Language", "items"], value: newItems })
            );
        },
        [items, dispatch, isItemDisabledInGeneral]
    );

    const handleReorder = React.useCallback(
        (newItems: POSLanguageItem[]) => {
            dispatch(
                updateConfig({ path: ["Language", "items"], value: newItems })
            );
        },
        [dispatch]
    );

    const isToggleDisabled = React.useCallback(
        (item: POSLanguageItem) =>
            isItemDisabledInGeneral(item.id) ||
            Boolean((item.isDefault || item.isFavorite) && item.active),
        [isItemDisabledInGeneral]
    );

    const getSubtitle = React.useCallback(
        (item: POSLanguageItem) =>
            item.isDefault ? t("Default") : undefined,
        [t]
    );

    return (
        <div className="h-100 pos-tab-layout__clz">
            <div>
                <div className="pos-section-title__clz">
                    {t("Languages choice")}
                </div>
            </div>

            <DraggableCardList
                droppableId="language"
                items={items}
                getTitle={getLanguageTitle}
                getSubtitle={getSubtitle}
                getIcon={getLanguageIcon}
                isToggleDisabled={isToggleDisabled}
                isGreyedOut={(item: POSLanguageItem) =>
                    isItemDisabledInGeneral(item.id)
                }
                onReorder={handleReorder}
                onToggle={handleToggle}
                testIdPrefix="language"
            />
        </div>
    );
}
